import { SearchSelectbox } from "cpl-react-common/components/Inputs/SearchSelectbox";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import SimpleModal from "cpl-react-common/components/Modals/SimpleModal";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  selectLoadingPermissions,
  selectLoadingUserGroups,
  updateUserGroupPermissionAssignment,
} from "./PermissionAssignmentsSlice";
import { useSelector } from "react-redux";

const newPermissionGroupValidationSchema = () =>
  Yup.object().shape(
    {
      userGroupID: Yup.string().required("A valid user group must be selected"),
      permissionID: Yup.number().required("A valid permission must be selected"),
    },
    []
  );

type NewPermissionGroupValues = {
  userGroupID: number | undefined;
  permissionID: number | undefined;
  granted: boolean;
};

const initialPermissionGroupValues: NewPermissionGroupValues = {
  userGroupID: undefined,
  permissionID: undefined,
  granted: false,
};

type NewPermissionModalProps = {
  showNewPermissionGroupModal: boolean;
  setShowNewPermissionGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
  filteredGroupList: {
    value: number;
    label: string;
  }[];
  permissionsList: {
    value: number;
    label: string;
  }[];
};

const NewPermissionGroupModal = (props: NewPermissionModalProps) => {
  const thunkDispatch = useThunkDispatch();
  const loadingUserGroups = useSelector(selectLoadingUserGroups);
  const loadingPermissions = useSelector(selectLoadingPermissions);

  function addGroupPermission({ userGroupID, permissionID, granted }: NewPermissionGroupValues) {
    if (userGroupID != undefined && permissionID != undefined) {
      thunkDispatch(
        updateUserGroupPermissionAssignment({
          userGroupID,
          permissionID,
          grant: granted,
        })
      );
      props.setShowNewPermissionGroupModal(false);
    }
  }

  return (
    <SimpleModal
      modalTitle="New Group Permission"
      showModal={props.showNewPermissionGroupModal}
      dismissModalCallback={() => props.setShowNewPermissionGroupModal(false)}
    >
      <Formik
        initialValues={initialPermissionGroupValues}
        validationSchema={newPermissionGroupValidationSchema()}
        onSubmit={(values) => addGroupPermission(values)}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <div className="row mb-6">
                <label className="col-lg-3 col-form-label required fw-bold">User Group:</label>
                <div className="col-lg-9">
                  <ActivityIndicator isBusy={loadingUserGroups}>
                    <SearchSelectbox
                      data-test-id="new-group-permission-modal__user-group-select"
                      selectOptions={props.filteredGroupList}
                      handleChange={(selection) => {
                        setFieldValue("userGroupID", selection.value);
                      }}
                    />
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <ErrorMessage name="userGroupID" />
                      </div>
                    </div>
                  </ActivityIndicator>
                </div>
              </div>
              <div className="row mb-6">
                <label className="col-lg-3 col-form-label required fw-bold">Permission:</label>
                <div className="col-lg-9">
                  <ActivityIndicator isBusy={loadingPermissions}>
                    <SearchSelectbox
                      data-test-id="new-group-permission-modal__permission-select"
                      selectOptions={props.permissionsList}
                      handleChange={(selection) => {
                        setFieldValue("permissionID", selection.value);
                      }}
                    />
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <ErrorMessage name="permissionID" />
                      </div>
                    </div>
                  </ActivityIndicator>
                </div>
              </div>
              <div className="row mb-6">
                <label className="col-lg-3 col-form-label fw-bold">Grant/Deny:</label>
                <div className="col-lg-9 d-flex align-items-center">
                  <Field
                    data-test-id="new-group-permission-modal__granted-checkbox"
                    name={"granted"}
                    className="d-flex form-control form-check-input"
                    type="checkbox"
                    checked={values.granted}
                  />
                </div>
              </div>
              <button
                data-test-id="new-group-permission-modal__add-button"
                type="submit"
                className="col-sm-3 btn btn-success btn-sm"
              >
                Add
              </button>
            </Form>
          );
        }}
      </Formik>
    </SimpleModal>
  );
};

export default NewPermissionGroupModal;
